import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import DetailToolBar from '../fw/DetailToolBar.react.js';
import DetailBox from '../fw/DetailBox.react.js';
import TableDetail from '../fw/TableDetail.react';
import DeleteButton from '../fw/DeleteButton.react';
import {wrapNotFound} from '../app/NotFoundHandler.react';

import urlMapping from '../app/URLMapping';
import types from '../../common/model-api/types';

import ExportMutation from '../channel-defs/ExportMutation.react';
import filesaver from 'filesaver.js';
import {FormattedMessage, injectIntl, defineMessages, IntlShape} from 'react-intl';
import ScreenDisplayer, {ScreenDisplayerListener} from '../view/box-utils/ScreenDisplayer.react';
import {getViewFragment, getDataSourceFragment} from '../view/box-utils/ScreenFragments.react';

import {sendToExtension} from '../app/ExtensionCommunicate';
import FakePanel from '../scenarios/FakePanel.react';
import mutationError from '../channel-defs/mutationError';
import * as BrowserPermission from "../BrowserPermission";
import {getLastOrganisationId} from '../app/LastOrganisationId';
import { User } from '../../common/types/types';

const messages = defineMessages({
    title: {id: 'screenView.title', 'defaultMessage': 'Screen'}
});

interface ScreenViewProps {
    intl: IntlShape;
    relay?: object;
    user: User;
}

class ScreenView extends React.Component<ScreenViewProps> {

    typeName = 'screens';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired
    };

    static contextTypes = {
        router: PropTypes.object
    };

    constructor() {
        super();
    }

    onClickExport = () => {
        let onSuccess = (response) => {
            let blob = new Blob([response.export.output], {type: "application/json;charset=utf-8"});
            filesaver.saveAs(blob, response.export.filename, true);
        };

        this.props.relay.commitUpdate(
            new ExportMutation({
                typeName: 'screens',
                id: this.props.user.organisation.channel.screen.realId
            }), {onSuccess: onSuccess, onFailure: mutationError}
        );
    };

    onDeleteSuccess = () => {
        /* inform extension to reload its configuration */
        sendToExtension({
            message: 'dativery-reload',
        });

        let path = urlMapping.getUrl(types['screens'], "list",  this.props.user.organisation.channel);
        this.context.router.push(path);
    };


    render() {
        const data = this.props.user.organisation.channel.screen;
        let type = types[this.typeName];

        let views = data.views;
        let dataSources = data.dataSources;

        return (
            <AppPanel breadcrumbParams={[
                {type, data},
                {type: types['channels'], data: this.props.user.organisation.channel},
                {type: types['organisations'], data: this.props.user.organisation}]
            } title={this.props.intl.formatMessage(messages.title)}>
                <div>
                    <DetailBox data={data} metadata={type} urlMapping={urlMapping}>
                        <DetailToolBar data={data} metadata={type} organisationId={this.props.user.organisation.realId} urlMapping={urlMapping}>
                            <DeleteButton
                                data={data}
                                deleteFatQuery={Relay.QL `fragment on DeleteScreenPayload {
                                                                updatedNode {
                                                                    id
                                                                },
                                                                channel {
                                                                    screens(first: 100) {
                                                                        edges {
                                                                            node {
                                                                                id
                                                                            }
                                                                        }
                                                                    }
                                                                }

                                                            }`}
                                deleteMutation={Relay.QL `mutation { deleteScreen }`}
                                metadata={type}
                                onSuccess={this.onDeleteSuccess}
                                organisationId={data.organisation_id}
                                types={types}
                                urlMapping={urlMapping}
                            />
                            {type.canUpdate(BrowserPermission.getPermissionObject(), data, data.organisationId) ? <button className="btn btn-default" onClick={this.onClickExport}><span className="fa fa-download"/> <FormattedMessage defaultMessage="Export" id="screens.export" /></button> : null}
                        </DetailToolBar>

                        <h2><FormattedMessage defaultMessage="Screen" id="screenView.title" /></h2>

                        <TableDetail columns={['channelEntityDef.entityDef.name', 'screenDef.name', 'operation', 'subOperation']} data={data} metadata={type} />
                    </DetailBox>

                    <FakePanel channelDefType={data.channelDef ? data.channelDef.type : null} sourceDefType={data.channelEntityDef && data.channelEntityDef.sourceDef ? data.channelEntityDef.sourceDef.type : null}>
                        <ScreenDisplayer
                            content={data.content}
                            isPreview
                            listener={new ScreenDisplayerListener()}
                            relay={this.props.relay}
                            viewsWithData={Object.keys(views).map((viewId) => {
                                let view = views[viewId];
                                let dataSource;

                                if (view['dataSource_id']) {
                                    dataSource = dataSources.find((dataSource) => dataSource['realId'] === view['dataSource_id']);
                                }

                                let result = ({
                                    view: view,
                                    data: '[]',
                                    entityDef: dataSource ? dataSource['primaryEntityDef'] : null,
                                    addon: '{}',
                                });

                                return result;
                            })}
                        />
                    </FakePanel>
                </div>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(ScreenView), (props) => {
    return props.user.organisation && props.user.organisation.channel && props.user.organisation.channel.screen;
}), {
    initialVariables: {
        organisationId: '',
        channelId: '',
        screenId: '',
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
		}

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    channel(id: $channelId) {
                        id,
                        organisation_id,
                        name,
                        screen(id: $screenId) {
                            id,
                            realId,
                            organisation_id,
                            channel_id,
                            channelEntityDef {
                                id,
                                entityDef {
                                    id,
                                    name
                                },
                                sourceDef {
                                    id,
                                    realId,
                                    type
                                },
                            },
                            channelDef {
                                id,
                                realId,
                                type
                            }
                            screenDef {
                                id,
                                name
                            },
                            channel_id,
                            subOperation,
                            operation,
                            content,
                            operation,
                            views {
                                ${getViewFragment()}
                            },
                            dataSources {
                                ${getDataSourceFragment()}
                            }
                        }
                    }
                }
            }
        `,
    },
});
